import { renderAuthScreen } from "./AuthScreen.js";
import { createLocalCleanup } from "./localCleanup.js";
import { canonicalizeNext } from "./safeNext.js";
import { SessionApi } from "./sessionApi.js";
import { parseSessionTimingConfig } from "./sessionConfig.js";
import { createSessionEvents } from "./sessionEvents.js";
import {
  createSessionGate,
  type SessionGate,
  type SessionState,
} from "./sessionGate.js";
import {
  browserWsAuth,
  clearVerifiedUser,
  rememberVerifiedUser,
} from "./sessionContext.js";
import type { SessionUser } from "./sessionTypes.js";
import {
  clearSessionLogout,
  clearSessionReverification,
  configureSessionLogout,
  configureSessionReverification,
} from "./sessionActions.js";

export interface ProtectedEntryGatePort {
  verify(reason: "boot" | "retry"): Promise<SessionState>;
  dispose(): void;
}

export interface ProtectedEntryOptions {
  readonly gate: ProtectedEntryGatePort;
  readonly protectedRoot: HTMLElement;
  readonly neutralRoot: HTMLElement;
  readonly loginUrl: string;
  readonly load: () => Promise<unknown>;
  readonly navigate: (url: string) => void;
  readonly rememberUser: (user: SessionUser) => void;
}

function showProbe(root: HTMLElement): void {
  root.replaceChildren(
    renderAuthScreen({
      kind: "message",
      status: "Проверяем сессию",
      title: "Проверяем сессию…",
      description:
        "Связываемся с сервером, прежде чем открыть защищённую часть GoCanvas.",
      loading: true,
    }),
  );
}

export async function runProtectedEntry(
  options: ProtectedEntryOptions,
  reason: "boot" | "retry" = "boot",
): Promise<void> {
  options.protectedRoot.hidden = true;
  options.protectedRoot.inert = true;
  showProbe(options.neutralRoot);
  const state = await options.gate.verify(reason);
  if (state.status === "authenticated") {
    options.rememberUser(state.user);
    options.neutralRoot.replaceChildren();
    options.neutralRoot.hidden = true;
    options.protectedRoot.inert = false;
    options.protectedRoot.hidden = false;
    await options.load();
    return;
  }
  if (state.status === "anonymous") {
    options.navigate(options.loginUrl);
    return;
  }
  if (state.status === "recoverable") {
    const coordinationTimeout =
      "code" in state.error &&
      state.error.code === "auth.refresh_coordination_timeout";
    options.neutralRoot.replaceChildren(
      renderAuthScreen({
        kind: "message",
        status: coordinationTimeout
          ? "Обновление сессии задерживается"
          : "Сеть недоступна",
        title: "Не удалось проверить сессию",
        description: coordinationTimeout
          ? "Другая вкладка не завершила обновление сессии. Вернитесь в неё или закройте её, затем повторите проверку."
          : "Проверьте соединение и попробуйте снова. Защищённые данные не загружены.",
        actions: [
          {
            label: "Повторить",
            autofocus: true,
            onActivate: () => void runProtectedEntry(options, "retry"),
          },
        ],
      }),
    );
  }
}

function baseEntry(base: string, entry: string): string {
  return `${base}${entry}`.replace(/\/{2,}/gu, "/");
}

export interface BrowserProtectedEntryOptions {
  readonly protectedRoot: HTMLElement;
  readonly neutralRoot: HTMLElement;
  readonly load: () => Promise<unknown>;
  readonly loginNext?: string;
}

interface EntryLocation {
  readonly origin: string;
  readonly pathname: string;
  readonly search: string;
  readonly hash: string;
}

export function protectedEntryLoginUrl(
  location: EntryLocation,
  base: string,
  fallback: string,
  requestedNext?: string,
): string {
  const next = canonicalizeNext(
    requestedNext ?? `${location.pathname}${location.search}${location.hash}`,
    { origin: location.origin, basePath: base, fallback },
  );
  return `${baseEntry(base, "login.html")}?next=${encodeURIComponent(next)}`;
}

interface ProtectedProductModule {
  readonly disposeProtectedEntry?: () => void;
}

export function createProtectedModuleLifecycle(load: () => Promise<unknown>): {
  loadOnce: () => Promise<void>;
  dispose: () => void;
} {
  let loading: Promise<void> | null = null;
  let disposeProduct: (() => void) | null = null;
  let disposed = false;
  const loadOnce = (): Promise<void> => {
    loading ??= load().then((module) => {
      const candidate = (module as ProtectedProductModule | null)
        ?.disposeProtectedEntry;
      if (typeof candidate !== "function") return;
      if (disposed) candidate();
      else disposeProduct = candidate;
    });
    return loading;
  };
  return {
    loadOnce,
    dispose: () => {
      if (disposed) return;
      disposed = true;
      disposeProduct?.();
      disposeProduct = null;
    },
  };
}

export function installBfcacheRestoreReload(target: EventTarget, reload: () => void): void {
  target.addEventListener("pageshow", (event) => {
    if ("persisted" in event && event.persisted === true) reload();
  });
}

export interface ProductUnauthorizedHandlerOptions {
  readonly gate: ProtectedEntryGatePort;
  readonly protectedRoot: HTMLElement;
  readonly neutralRoot: HTMLElement;
  readonly loginUrl: string;
  readonly navigate: (url: string) => void;
  readonly rememberUser: (user: SessionUser) => void;
  readonly disposeProduct: () => void;
  readonly clearIdentity: () => void;
  readonly reload: () => void;
}

export interface ProductUnauthorizedHandler {
  (): Promise<void>;
  quiesce(): void;
}

export function createProductUnauthorizedHandler(
  options: ProductUnauthorizedHandlerOptions,
): ProductUnauthorizedHandler {
  let pending: Promise<void> | null = null;
  let quiesced = false;
  const handler = (): Promise<void> => {
    if (quiesced) return Promise.resolve();
    if (pending !== null) return pending;
    options.disposeProduct();
    options.clearIdentity();
    options.protectedRoot.hidden = true;
    options.protectedRoot.inert = true;
    options.neutralRoot.hidden = false;
    pending = runProtectedEntry({
      gate: options.gate,
      protectedRoot: options.protectedRoot,
      neutralRoot: options.neutralRoot,
      loginUrl: options.loginUrl,
      load: async () => options.reload(),
      navigate: options.navigate,
      rememberUser: options.rememberUser,
    }, "retry").finally(() => {
      pending = null;
    });
    return pending;
  };
  handler.quiesce = (): void => {
    quiesced = true;
    options.disposeProduct();
  };
  return handler;
}

export function startBrowserProtectedEntry(
  options: BrowserProtectedEntryOptions,
): SessionGate {
  const env = import.meta.env as Readonly<Record<string, string | undefined>>;
  const timings = parseSessionTimingConfig(env);
  const base = import.meta.env.BASE_URL || "/";
  const fallback =
    import.meta.env.VITE_AUTH_FALLBACK_PATH ||
    baseEntry(base, "projects.html");
  const loginUrl = protectedEntryLoginUrl(
    globalThis.location,
    base,
    fallback,
    options.loginNext,
  );
  const events = createSessionEvents({
    leaseMs: timings.crossTabRefreshLeaseMs,
  });
  const api = new SessionApi({
    timings,
    wsAuthAdapter: browserWsAuth,
    origin: globalThis.location.origin,
  });
  const protectedSurface = {
    mount: () => undefined,
    unmount: () => {
      options.protectedRoot.hidden = true;
      options.protectedRoot.inert = true;
    },
  };
  const product = createProtectedModuleLifecycle(options.load);
  const cleanup = createLocalCleanup({
    wsAuthAdapter: browserWsAuth,
    identityCache: { clear: clearVerifiedUser },
    cancelPending: product.dispose,
    protectedSurface,
  });
  const gate = createSessionGate({
    api,
    timings,
    protectedSurface,
    cleanup,
    events,
  });
  let loaded = false;
  const loadOnce = async (): Promise<void> => {
    await product.loadOnce();
    loaded = true;
  };
  const entryOptions: ProtectedEntryOptions = {
    gate,
    protectedRoot: options.protectedRoot,
    neutralRoot: options.neutralRoot,
    loginUrl,
    load: loadOnce,
    navigate: (url) => globalThis.location.replace(url),
    rememberUser: rememberVerifiedUser,
  };
  const requestSessionReverification = createProductUnauthorizedHandler({
    gate,
    protectedRoot: options.protectedRoot,
    neutralRoot: options.neutralRoot,
    loginUrl,
    navigate: (url) => globalThis.location.replace(url),
    rememberUser: rememberVerifiedUser,
    disposeProduct: product.dispose,
    clearIdentity: clearVerifiedUser,
    reload: () => globalThis.location.reload(),
  });
  const showLogoutPending = (): void => {
    options.protectedRoot.hidden = true;
    options.protectedRoot.inert = true;
    options.neutralRoot.hidden = false;
    options.neutralRoot.replaceChildren(
      renderAuthScreen({
        kind: "message",
        status: "Отзыв сессии не подтверждён",
        title: "Не удалось завершить сессию на сервере",
        description: "Защищённая часть уже закрыта в этой вкладке.",
        actions: [
          {
            label: "Повторить выход",
            autofocus: true,
            onActivate: () => void requestLogout(),
          },
          {
            label: "Продолжить текущую сессию",
            secondary: true,
            // localCleanup has disposed product transports. A full reload
            // creates a fresh lifecycle and starts with authoritative /me.
            onActivate: () => globalThis.location.reload(),
          },
        ],
      }),
    );
  };
  const requestLogout = async (): Promise<void> => {
    requestSessionReverification.quiesce();
    options.protectedRoot.hidden = true;
    options.protectedRoot.inert = true;
    options.neutralRoot.hidden = false;
    options.neutralRoot.replaceChildren(
      renderAuthScreen({
        kind: "message",
        status: "Завершаем сессию",
        title: "Завершаем сессию…",
        description: "Подтверждаем выход на сервере.",
        loading: true,
      }),
    );
    const state = await gate.logout();
    if (state.status === "anonymous") {
      globalThis.location.replace(loginUrl);
    } else if (state.status === "logout-pending") {
      showLogoutPending();
    }
  };
  configureSessionLogout(requestLogout);
  configureSessionReverification(requestSessionReverification);
  const unsubscribe = gate.subscribe((state) => {
    if (state.status === "anonymous") {
      options.protectedRoot.hidden = true;
      options.protectedRoot.inert = true;
      globalThis.location.replace(loginUrl);
    } else if (state.status === "recoverable" && loaded) {
      product.dispose();
      options.protectedRoot.hidden = true;
      options.protectedRoot.inert = true;
      options.neutralRoot.hidden = false;
      options.neutralRoot.replaceChildren(
        renderAuthScreen({
          kind: "message",
          status: "Сессия требует проверки",
          title: "Не удалось подтвердить сессию",
          description:
            "Защищённая часть временно закрыта. Проверьте соединение и повторите.",
          actions: [
            {
              label: "Повторить",
              autofocus: true,
              onActivate: () => globalThis.location.reload(),
            },
          ],
        }),
      );
    }
  });
  void runProtectedEntry(entryOptions);
  // pagehide disposes the protected runtime. A BFCache return must start a
  // fresh session check instead of revealing the disposed document.
  installBfcacheRestoreReload(globalThis, () => globalThis.location.reload());
  globalThis.addEventListener(
    "pagehide",
    () => {
      product.dispose();
      gate.dispose();
      events.dispose();
      unsubscribe();
      clearSessionLogout();
      clearSessionReverification();
    },
    { once: true },
  );
  return gate;
}
